import '@data/Simulations/pipeline/blocks/RemoveSensorsBlock';
import type { RemoveSensorsByIdBlockParameters } from '@data/Simulations/pipeline/blocks/RemoveSensorsBlock';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { LayerKind, type BlockParameters } from '@visualizer/types';
import { FC, useEffect, useMemo } from 'react';
import { usePipelineBlockParams } from './usePipelineBlockParams';

type RemoveSensorsByIdParametersProps = {
    blockId: string;
};

export const getRemoveSensorsByIdDefaultParams = (): Pick<RemoveSensorsByIdBlockParameters, 'sensorIds' | 'isInverted'> => ({
    sensorIds: new Set<number>(),
    isInverted: false,
});

const RemoveSensorsByIdParameters: FC<RemoveSensorsByIdParametersProps> = ({ blockId }) => {
    // Controllers:
    const designSchemesController = ControllerManager.getInstance().getDesignSchemeController();
    const pipelineController = useMemo(() => ControllerManager.getInstance().getPipelineController(), []);

    const params = usePipelineBlockParams<RemoveSensorsByIdBlockParameters>(blockId);
    const updateParams = (partial: Partial<RemoveSensorsByIdBlockParameters>) => {
        pipelineController.updateBlockParams(blockId, partial as BlockParameters);
    };

    useEffect(() => {
        if (!params) return;

        // Generate Set of SensorDatasId:
        const ids = new Set(
            designSchemesController.getDesignSchemeSets().flatMap((set) => {
                const designSchemes = designSchemesController.getDesignSchemes(set.getID());
                return designSchemes?.flatMap((scheme) => scheme.getSensorDatasFor(LayerKind.MAIN).map((sd) => sd.getID()))!;
            })
        );

        // Update params (passed to the block) when the Set changes:
        const existing = params.sensorIds ?? new Set<number>();

        let isDifferent = false;

        if (existing.size !== ids.size) {
            isDifferent = true;
        } else {
            for (const id of ids) {
                if (!existing.has(id)) {
                    isDifferent = true;
                    break;
                }
            }
        }

        if (isDifferent) {
            updateParams({ sensorIds: ids });
        }
    }, [params]);

    // Empty UI:
    return null;
};

export default RemoveSensorsByIdParameters;
